import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, TextField, Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";
import { useApi } from "lib/api";

function CanceladosToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function buildCanceladosColumns() {
  return [
    { field: "id", headerName: "DOC #", width: 85, align: "center", headerAlign: "center" },
    { field: "fecha", headerName: "Fecha", type: "string", width: 115, align: "center", headerAlign: "center" },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 130 },
    { field: "apellidos", headerName: "Apellidos", flex: 1, minWidth: 130 },
    { field: "cantsol", headerName: "Cantidad", type: "string", width: 115, align: "right", headerAlign: "right" },
    { field: "descuento", headerName: "Descuento", type: "string", width: 115, align: "right", headerAlign: "right" },
    { field: "restante", headerName: "Restante", type: "string", width: 115, align: "right", headerAlign: "right" },
    { field: "pagado", headerName: "Pagado", type: "string", width: 115, align: "right", headerAlign: "right" },
    { field: "plazo", headerName: "Plazo", type: "string", width: 100, align: "center", headerAlign: "center" },
    { field: "interes", headerName: "Interés", type: "string", width: 100, align: "right", headerAlign: "right" },
    { field: "status", headerName: "Status", type: "string", width: 110, align: "center", headerAlign: "center" },
  ];
}

function ListarCanceladoFecha() {
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState(null);
  const [sucursal, setSucursal] = useState([]);
  // Financiamientos de motores (General) o préstamos personales
  // (prestamo.ppersonal): mismo flag que el informe de pagos.
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);

  const [rows, setRows] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { post } = useApi();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const columns = useMemo(() => buildCanceladosColumns(), []);

  useEffect(() => {
    post("/api/recuperasucursales", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: nosucursal,
    }).then(function ({ status, data }) {
      if (status === 200) {
        setSucursal(data["sucursales"]);
      }
      if (status === 400) {
        setRows([]);
        alert(data["Error"]);
      }
    });
  }, []);

  function procesarrequerimiento() {
    const sucursalSeleccionada =
      typeof nosucursal === "string" ? nosucursal : nosucursal?.label || "";
    post("/api/listarcanceladosporfechas", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: sucursalSeleccionada,
      prestamospersonales: prestamosPersonales,
    }).then(function ({ status, data }) {
      if (status === 200) {
        setRows(data["prestamocancelados"]);
      }
      if (status === 400) {
        setRows([]);
        alert(data["Error"]);
      }
    });
  }

  // Preview con anchos proporcionales (los 100px fijos por columna se salían
  // del A4), columnas sin duplicar, totales sobre los campos reales del
  // backend (cantsol/descuento/restante/pagado) y sin `fixed` en las filas
  // de datos (en react-pdf repite el elemento en cada página).
  const MyDocument = () => {
    const parseN = (v) => parseFloat(String(v ?? "0").split(",").join("")) || 0;
    const fmtN = (v) => parseN(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const tcantidad = rows.reduce((acc, r) => acc + parseN(r.cantsol), 0);
    const tdescuento = rows.reduce((acc, r) => acc + parseN(r.descuento), 0);
    const trestante = rows.reduce((acc, r) => acc + parseN(r.restante), 0);
    const tpagado = rows.reduce((acc, r) => acc + parseN(r.pagado), 0);
    const W = { prest: "8%", fecha: "10%", cliente: "24%", cant: "12%", desc: "11%", rest: "12%", pag: "12%", plazo: "6%", interes: "5%" };
    const celda = { padding: 2 };
    const num = { ...celda, textAlign: "right" };
    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document>
          <Page size="A4" style={{ backgroundColor: "#FFFFFF", paddingTop: 15, paddingBottom: 25, paddingHorizontal: 18 }}>
            <View style={{ textAlign: "center", fontSize: 14, textTransform: "uppercase" }} fixed>
              <Text>{nombreNegocio}</Text>
            </View>
            <View style={{ textAlign: "center", fontSize: 9, textTransform: "uppercase", marginBottom: 8 }} fixed>
              <Text>Préstamos cancelados por fecha</Text>
            </View>
            <View style={{ flexDirection: "row", fontSize: 7.5, backgroundColor: "#E6EAF1", color: "black", fontWeight: "bold" }} fixed>
              <Text style={{ ...celda, width: W.prest }}>Prest#</Text>
              <Text style={{ ...celda, width: W.fecha }}>Fecha</Text>
              <Text style={{ ...celda, width: W.cliente }}>Cliente</Text>
              <Text style={{ ...num, width: W.cant }}>Cantidad</Text>
              <Text style={{ ...num, width: W.desc }}>Descuento</Text>
              <Text style={{ ...num, width: W.rest }}>Restante</Text>
              <Text style={{ ...num, width: W.pag }}>Pagado</Text>
              <Text style={{ ...num, width: W.plazo }}>Plazo</Text>
              <Text style={{ ...num, width: W.interes }}>Int.</Text>
            </View>
            {rows.map((filas, i) => (
              <View key={i} wrap={false} style={{ flexDirection: "row", fontSize: 7.5, color: "black", borderBottom: "0.5px solid #DDD" }}>
                <Text style={{ ...celda, width: W.prest }}>{filas.id}</Text>
                <Text style={{ ...celda, width: W.fecha }}>{filas.fecha}</Text>
                <Text style={{ ...celda, width: W.cliente }}>{`${filas.nombres || ""} ${filas.apellidos || ""}`.trim()}</Text>
                <Text style={{ ...num, width: W.cant }}>{fmtN(filas.cantsol)}</Text>
                <Text style={{ ...num, width: W.desc }}>{fmtN(filas.descuento)}</Text>
                <Text style={{ ...num, width: W.rest }}>{fmtN(filas.restante)}</Text>
                <Text style={{ ...num, width: W.pag }}>{fmtN(filas.pagado)}</Text>
                <Text style={{ ...num, width: W.plazo }}>{filas.plazo}</Text>
                <Text style={{ ...num, width: W.interes }}>{filas.interes}</Text>
              </View>
            ))}
            <View style={{ flexDirection: "row", fontSize: 7.5, color: "black", marginTop: 6, fontWeight: "bold" }}>
              <Text style={{ ...celda, width: W.prest }}>Totales</Text>
              <Text style={{ ...celda, width: W.fecha }}> </Text>
              <Text style={{ ...celda, width: W.cliente }}>{`${rows.length} préstamos`}</Text>
              <Text style={{ ...num, width: W.cant }}>{fmtN(tcantidad)}</Text>
              <Text style={{ ...num, width: W.desc }}>{fmtN(tdescuento)}</Text>
              <Text style={{ ...num, width: W.rest }}>{fmtN(trestante)}</Text>
              <Text style={{ ...num, width: W.pag }}>{fmtN(tpagado)}</Text>
              <Text style={{ ...num, width: W.plazo }}> </Text>
              <Text style={{ ...num, width: W.interes }}> </Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (rows.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }
  const totalCantidad = rows.reduce(
    (acc, item) => acc + (parseFloat(String(item.cantsol || "0").split(",").join("")) || 0),
    0
  );
  const totalInteres = rows.reduce(
    (acc, item) => acc + (parseFloat(String(item.interes || "0").split(",").join("")) || 0),
    0
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Listar Cancelados por Fechas"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={fpInputSx}
                />
              </Grid>
              {/* General = financiamientos de motores; Personal = préstamos
                  personales (mismo corte que el informe de pagos). */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de préstamo"
                  value={prestamosPersonales ? "personal" : "general"}
                  onChange={(event) => setPrestamosPersonales(event.target.value === "personal")}
                  sx={fpInputSx}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="general">General</option>
                  <option value="personal">Personal</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.label || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionLabel = typeof option === "string" ? option : option?.label;
                    const valueLabel = typeof value === "string" ? value : value?.label;
                    return optionLabel === valueLabel;
                  }}
                  onChange={(event, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total cantidad"
              value={`$${totalCantidad.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={PaymentIcon}
              accent={FP.color.navy}
            />
            <StatCard
              label="Total interés"
              value={`$${totalInteres.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={TrendingUpIcon}
              accent={FP.color.warning}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Listado de préstamos cancelados
            </Typography>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => (row.id != null ? String(row.id) : `row-${row.fecha}-${row.nombres}`)}
                    slots={{ toolbar: CanceladosToolbar }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 720, sm: "100%" }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                  {rows.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `row-${row.fecha}-${row.nombres}-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", mt: 0.25, wordBreak: "break-word" }}>
                            {row.fecha} · {[row.nombres, row.apellidos].filter(Boolean).join(" ")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flexShrink: 0,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: FP.color.surfaceAlt,
                            color: FP.color.textSecondary,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                          }}
                        >
                          {row.status || "—"}
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Cantidad", row.cantsol],
                          ["Descuento", row.descuento],
                          ["Restante", row.restante],
                          ["Pagado", row.pagado],
                          ["Plazo", row.plazo],
                          ["Interés", row.interes],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
export default ListarCanceladoFecha;
